import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Typography, theme } from 'antd';
import { IconWriting, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const { Text } = Typography;

export interface SignaturePadHandle {
  /** The drawn signature as a PNG Blob, or null if nothing has been drawn. */
  toBlob: () => Promise<Blob | null>;
  clear: () => void;
}

interface SignaturePadProps {
  /** Told on every transition between "nothing drawn" and "something drawn". */
  onSignedChange?: (signed: boolean) => void;
}

// Drawn at 2x the CSS size so a signature doesn't come out of a phone
// screen looking like it was traced through frosted glass -- the canvas
// backing store is scaled up and the context scaled down to match, the
// standard devicePixelRatio trick pinned to a constant rather than the real
// ratio so the uploaded image is the same size from every device.
const PIXEL_RATIO = 2;
const CANVAS_HEIGHT = 160;
const STROKE_WIDTH = 2.2;

/**
 * Finger-drawn signature capture for the digital albarán -- the one part of
 * a paper delivery note people actually picture when you say "albarán".
 *
 * Deliberately dumb: it draws, it clears, and it hands back a PNG when
 * asked. It has no idea what is being signed, and nothing here is what
 * makes the acknowledgement binding -- that is the authenticated API call
 * recording who answered, when, and with what outcome (see
 * CheckInOutReport.AcknowledgementSignatureBlobPath on the backend). This
 * is the human-facing ceremony on top of it, which on a delivery to a site
 * is worth more than it looks: it is the moment both people stop and agree
 * they are looking at the same machine.
 */
const SignaturePad = React.forwardRef<SignaturePadHandle, SignaturePadProps>(({ onSignedChange }, ref) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [signed, setSigned] = useState(false);

  const contextOf = (canvas: HTMLCanvasElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = STROKE_WIDTH;
    ctx.strokeStyle = token.colorText;
    return ctx;
  };

  // Sized from the element's own laid-out width rather than a fixed number,
  // so it fills whatever column it lands in (a 480px sheet on a phone, a
  // narrower antd Modal on desktop) without a media query.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = canvas.clientWidth;
    canvas.width = width * PIXEL_RATIO;
    canvas.height = CANVAS_HEIGHT * PIXEL_RATIO;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(PIXEL_RATIO, PIXEL_RATIO);
  }, []);

  const pointFromEvent = (canvas: HTMLCanvasElement, e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas && contextOf(canvas);
    if (!canvas || !ctx) return;
    // Captured so a stroke that wanders off the canvas mid-signature keeps
    // being delivered here instead of being dropped the moment the finger
    // crosses the border -- signatures routinely overshoot the box.
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = pointFromEvent(canvas, e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    if (!signed) {
      setSigned(true);
      onSignedChange?.(true);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas && contextOf(canvas);
    if (!canvas || !ctx) return;
    const { x, y } = pointFromEvent(canvas, e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handlePointerUp = () => {
    drawingRef.current = false;
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSigned(false);
    onSignedChange?.(false);
  }, [onSignedChange]);

  useImperativeHandle(ref, () => ({
    clear,
    toBlob: () =>
      new Promise<Blob | null>((resolve) => {
        const canvas = canvasRef.current;
        if (!canvas || !signed) {
          resolve(null);
          return;
        }
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      }),
  }));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <Text type="secondary" style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <IconWriting size={15} />
          {translate(t, TRANSLATIONS.HANDOVER_SIGNATURE_LABEL)}
        </Text>
        {signed && (
          <Button type="text" size="small" icon={<IconRefresh size={14} />} onClick={clear}>
            {translate(t, TRANSLATIONS.HANDOVER_SIGNATURE_CLEAR)}
          </Button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          display: 'block',
          width: '100%',
          height: CANVAS_HEIGHT,
          borderRadius: 16,
          background: token.colorFillQuaternary,
          border: `1px dashed ${token.colorBorder}`,
          // Without this the browser claims the gesture for scrolling and
          // the canvas never sees a move event on a touchscreen -- which is
          // every device this is actually used on.
          touchAction: 'none',
          cursor: 'crosshair',
        }}
      />
    </div>
  );
});

SignaturePad.displayName = 'SignaturePad';

export default SignaturePad;
